import { Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { FotografiaDeInforme } from "@/lib/informes";

import { CampoDeLeyenda } from "./CampoDeLeyenda";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { ZonaDeSoltarImagenes } from "./RecuadroDeSoltar";
import { formatearPeso } from "./ZonaDeImagen";

interface GaleriaDeFotografiasProps {
  titulo: string;
  /** La bajada bajo el título. */
  ayuda?: string;
  /** El texto del recuadro de soltar. Sin él, "{titulo} · JPG, PNG o WEBP". */
  marcador?: string;
  fotografias: FotografiaDeInforme[];
  /** Arma la url de lectura de cada foto. La pantalla sabe el informe; esto no. */
  urlDe: (fotografia: FotografiaDeInforme) => string;
  /** Si hay una subida o un borrado en curso en ESTE bloque. */
  ocupado?: boolean;
  /** El rechazo del NAVEGADOR (tipo y peso), o `null`. */
  error?: string | null;
  /** Se llama UNA VEZ por archivo y se espera a que termine antes del siguiente. */
  onArchivo: (archivo: File) => void | Promise<unknown>;
  onQuitar: (fotografiaId: number) => void;
  /** Con él, cada foto lleva un campo de leyenda que se guarda solo. */
  onLeyenda?: (fotografiaId: number, leyenda: string | null) => void;
}

/**
 * Un bloque de fotografías del expediente: VARIAS por encuadre.
 *
 * LLEVA EL MISMO DISEÑO QUE EL RECUADRO DE IMAGEN (`TarjetaDeImagen plana`):
 * misma tarjeta, misma cabecera con el chip gris "Se guarda al soltarla" y la
 * misma zona punteada (`RecuadroDeSoltar`). SIN número: el diseño numera 01–06
 * y esta tarjeta no está entre ellas.
 *
 * ES EL HERMANO DE `TarjetaDeImagen` Y NO UNA VARIANTE SUYA, y la diferencia
 * es la cardinalidad. Aquella muestra UNA imagen y su botón dice "Reemplazar";
 * acá se AGREGAN y se QUITAN, porque de un mismo recorrido salen diez fotos
 * del terreno: son diez hechos distintos, no diez versiones de uno solo.
 * Meterlas en el mismo componente obligaría a un `if` sobre la cardinalidad en
 * cada línea del render.
 *
 * LO QUE SÍ COMPARTE es el formato del peso y el rechazo del navegador: ese
 * último lo aplica QUIEN LLAMA, con `motivoDeRechazoDeLaImagen` de
 * `TarjetaDeImagen`, y llega acá ya resuelto en `error`. Así este componente
 * no conoce los límites del servidor y hay un solo lugar que los espeja.
 *
 * PRESENTACIONAL: no sabe de la API. Recibe las fotos y avisa qué pasó.
 */
export function GaleriaDeFotografias({
  titulo,
  ayuda,
  marcador,
  fotografias,
  urlDe,
  ocupado = false,
  error,
  onArchivo,
  onQuitar,
  onLeyenda,
}: GaleriaDeFotografiasProps) {
  /** Una a una: cada subida mueve la lista y la bandera de ocupado. */
  async function subir(archivos: File[]) {
    for (const archivo of archivos) {
      try {
        await onArchivo(archivo);
      } catch {
        // Quien llama ya avisó el fallo de ese archivo; los demás siguen.
      }
    }
  }

  return (
    <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
      <EncabezadoNumerado
        idDelTitulo={`galeria-${titulo}`}
        titulo={titulo}
        bajada={ayuda}
        acciones={
          <span className="bg-muted text-ink-muted rounded-[0.4375rem] px-2.5 py-[0.3125rem] text-[0.65625rem] font-bold">
            Se guarda al soltarla
          </span>
        }
      />

      <div className="mt-3.5 space-y-3">
        {fotografias.length > 0 && (
          /* MINIATURAS y no nombres de archivo: de estas fotos no se guarda el
             nombre original y la miniatura responde la única pregunta que uno
             se hace acá, que es si la foto es la del predio correcto. */
          <ul className="flex flex-wrap gap-3">
            {fotografias.map((fotografia) => (
              <li
                key={fotografia.id}
                className={`flex flex-col items-center gap-1 ${onLeyenda ? "w-36" : ""}`}
              >
                <a
                  href={urlDe(fotografia)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Ampliar fotografía ${fotografia.id}`}
                >
                  <img
                    src={urlDe(fotografia)}
                    alt=""
                    className="border-border size-20 rounded-[0.5625rem] border object-cover"
                  />
                </a>
                {onLeyenda && (
                  <CampoDeLeyenda
                    valor={fotografia.leyenda ?? null}
                    etiqueta={`Leyenda de la fotografía ${fotografia.id}`}
                    onGuardar={(leyenda) => onLeyenda(fotografia.id, leyenda)}
                  />
                )}
                <span className="text-ink-hint font-mono text-[0.6875rem]">
                  {formatearPeso(fotografia.bytes)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive h-7"
                  aria-label={`Quitar la fotografía ${fotografia.id}`}
                  disabled={ocupado}
                  onClick={() => onQuitar(fotografia.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Quitar
                </Button>
              </li>
            ))}
          </ul>
        )}

        <ZonaDeSoltarImagenes
          ariaLabel={`Adjuntar fotos a ${titulo}`}
          marcador={marcador ?? `${titulo} · JPG, PNG o WEBP`}
          ocupado={ocupado}
          onArchivos={subir}
        />

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    </section>
  );
}
